/// <reference path="./css-modules.d.ts" />
import "./css/variables.tailwind.css";

import Asset from "./components/Asset";
import {
    AnimatedBlock,
    AnimatedHTML,
    AnimatedHTMLLines,
    AnimatedLines,
    AnimatedStrongHTMLLines,
    AnimatedWords,
    contentWidth,
    directionalWordsContainerVariants,
    directionalWordVariants,
    layoutStrongHTMLLines,
    nextDelay,
    StrongHTMLLines,
    slideTextPresenceExit,
    splitPretextWords,
} from "./components/animation/AnimatedWords";
import Background from "./components/Background";
import Badge from "./components/Badge";
import {
    AccommodationDetails,
    AddressDetails,
    Attachments,
    Body,
    Header,
    Images,
} from "./components/Booking";
import { BookingOptionPicker } from "./components/BookingOptionPicker";
import { BottomSheet } from "./components/BottomSheet";
import Button from "./components/Button";
import CallToAction from "./components/CallToAction";
import ChatInput from "./components/ChatInput";
import ChatMessage from "./components/ChatMessage";
import ChatMessagePill from "./components/ChatMessagePill";
import { Checkbox } from "./components/Checkbox";
import ContentCenter from "./components/ContentCenter";
import { Flex, FlexInline } from "./components/Flex";
import { FloatingCTAButton } from "./components/FloatingCTAButton";
import Heading from "./components/Heading";
import Icon from "./components/Icon";
import ImageCollection from "./components/ImageCollection";
import ImageFullSizeButton from "./components/ImageFullSizeButton";
import Label from "./components/Label";
import { LazyImage } from "./components/LazyImage";
import Line from "./components/Line";
import LoadingSpinner from "./components/LoadingSpinner";
import NewMessagesLabel from "./components/NewMessagesLabel";
import Paragraph from "./components/Paragraph";
import { PoweredByAlbertine } from "./components/PoweredByAlbertine";
import ProposalBooking from "./components/ProposalBooking";
import { ProposalHeader } from "./components/ProposalHeader";
import { PhoneNumberInput } from "./components/phone/PhoneNumberInput";
import RemoteImage from "./components/RemoteImage";
import RequestStatusChatBadge from "./components/RequestStatusChatBadge";
import SpotlightItem from "./components/SpotlightItem";
import { Switch } from "./components/Switch";
import TextAreaInput from "./components/TextAreaInput";
import TextLink from "./components/TextLink";
import Timeline, { type TimelineItemDateProps } from "./components/Timeline";
import {
    BottomSheetProvider,
    useBottomSheetContext,
} from "./context/BottomSheetContext";
import { useHashRoute } from "./hooks/useHashRoute";
import NotFoundScreen from "./screens/NotFoundScreen";
import Public from "./templates/Public";
import {
    formatKeyDetailForAltText,
    generateAltTextWithKeyDetail,
    generateAvatarAltText,
    generateBookingImageAltText,
    generateUpcomingBookingAltText,
} from "./utils/accessibility.util";
import { cn } from "./utils/cn.util";
import { takeIfAnyPredicateMatches } from "./utils/filter.util";
import { formatPrice } from "./utils/formatPrice";
import { cdnImage } from "./utils/image.utils";
import { publicBookingId } from "./utils/publicId.util";
import { sortByDateComparator } from "./utils/sort.util";
import textFormat, {
    combineWith,
    defaultSeparator,
    timeDateSeparator,
} from "./utils/textFormatter.util";
import { scrollRefToBottom, scrollToElement } from "./utils/window.util";

export * from "./components/auth";
export * from "./components/phone";
export { useHashRoute } from "./hooks/useHashRoute";

export * from "./package/albertine-i18n";
export * from "./package/albertine-tenant";
export {
    formatKeyDetailForAltText,
    generateAltTextWithKeyDetail,
    generateBookingImageAltText,
} from "./utils/accessibility.util";
export type { FormattedPrice, PriceLike } from "./utils/formatPrice";
export { formatPrice } from "./utils/formatPrice";
export { tagDisplayName } from "./utils/tagDisplayName";
export type { TimelineItemDateProps };
export const sharedKeys = {
    cssFileManifestKey: "lib/index.ts",
};

export const Templates = {
    Public,
    Utils: {
        publicBookingId,
    },
};

export const Lib = {
    Heading,
    Paragraph,
    Label,
    Badge,
    Button,
    Background,
    Icon,
    Flex,
    Line,
    FlexInline,
    NotFoundScreen,
    Proposal: {
        Header: ProposalHeader,
        ProposalBooking,
    },
    CallToAction,
    ChatMessage,
    ChatInput,
    ChatMessagePill,
    ContentCenter,
    Asset,
    TextLink,
    RemoteImage,
    SpotlightItem,
    ImageCollection,
    TextAreaInput,
    Booking: {
        Header,
        Images,
        Attachments,
        AccommodationDetails,
        AddressDetails,
        Body,
    },
    Utils: {
        Accessibility: {
            formatKeyDetailForAltText,
            generateAltTextWithKeyDetail,
            generateBookingImageAltText,
            generateUpcomingBookingAltText,
            generateAvatarAltText,
        },
        Strings: {
            combineWith,
            defaultSeparator,
            timeDateSeparator,
        },
        Sorting: {
            sortByDateComparator,
        },
        Filtering: {
            takeIfAnyPredicateMatches,
        },
        TextFormatter: textFormat,
        Price: {
            formatPrice,
        },
        Window: {
            scrollRefToBottom,
            scrollToElement,
        },
        Styles: {
            cn,
        },
        Image: {
            cdnImage,
        },
    },
    LazyImage,
    NewMessagesLabel,
    RequestStatusChatBadge,
    LoadingSpinner,
    ImageFullSizeButton,
    BottomSheet,
    Context: {
        BottomSheetProvider,
        useBottomSheetContext,
    },
    BookingOptionPicker,
    Timeline,
    Switch,
    PoweredByAlbertine,
    Checkbox,
    FloatingCTAButton,
    PhoneNumberInput,
    Animation: {
        Block: AnimatedBlock,
        Words: AnimatedWords,
        Lines: AnimatedLines,
        HTML: AnimatedHTML,
        HTMLLines: AnimatedHTMLLines,
        StrongHTMLLines,
        StrongHTMLLinesAnimated: AnimatedStrongHTMLLines,
        layoutStrongHTMLLines,
        nextDelay,
        contentWidth,
        splitPretextWords,
        directionalWordVariants,
        directionalWordsContainerVariants,
        slideTextPresenceExit,
    },
    Hooks: {
        useHashRoute,
    },
};

export { default as Asset } from "./components/Asset";
